/**
 * Enhanced authentication utilities with cookie-based JWT and CSRF protection.
 */

import { getApiBaseUrl } from "./http";
import { preferenceService } from "../../services/preferenceService";

// ---------------------------------------------------------------------------
// Auth Logout Guard — prevents cascading auth:logout events from multiple
// in-flight requests all failing at once (e.g., preferences + conversations).
// ---------------------------------------------------------------------------
let _authLogoutDispatched = false;

const dispatchAuthLogout = () => {
  if (_authLogoutDispatched) return;
  _authLogoutDispatched = true;
  window.dispatchEvent(new Event('auth:logout'));
};

const resetAuthLogoutGuard = () => {
  _authLogoutDispatched = false;
};

// ---------------------------------------------------------------------------
// CSRF Token Management
// ---------------------------------------------------------------------------
let csrfToken = null;
const CSRF_STORAGE_KEY = 'csrf_token';

/**
 * Get CSRF token from memory, sessionStorage, or cookie.
 * In cross-origin deployments (SWA + Container App), document.cookie
 * cannot see cookies set on the API domain, so we persist the token
 * in sessionStorage as a reliable fallback.
 */
export const getCSRFToken = () => {
  if (csrfToken) return csrfToken;

  // Try sessionStorage (survives page refresh, works cross-origin)
  try {
    const stored = sessionStorage.getItem(CSRF_STORAGE_KEY);
    if (stored) {
      csrfToken = stored;
      return stored;
    }
  } catch { /* ignore */ }

  // Try to read from cookie (only works same-origin)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      csrfToken = value;
      return value;
    }
  }

  return null;
};

/**
 * Set CSRF token from response header or login response.
 * Persists to sessionStorage for cross-origin reliability.
 */
export const setCSRFToken = (token) => {
  csrfToken = token;
  try {
    if (token) {
      sessionStorage.setItem(CSRF_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(CSRF_STORAGE_KEY);
    }
  } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// User Info Management (still use localStorage for user info, not tokens)
// ---------------------------------------------------------------------------
const AUTH_USER_KEY = "auth_user";

export const saveAuthUser = (user) => {
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const getAuthUser = () => {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearAuthUser = () => {
  localStorage.removeItem(AUTH_USER_KEY);
};

// ---------------------------------------------------------------------------
// Session Management
// ---------------------------------------------------------------------------
const SESSION_ID_KEY = "session_id";

export const saveSessionId = (sessionId) => {
  if (sessionId) {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
};

export const getSessionId = () => {
  return sessionStorage.getItem(SESSION_ID_KEY);
};

export const clearSession = () => {
  sessionStorage.removeItem(SESSION_ID_KEY);
  sessionStorage.removeItem(CSRF_STORAGE_KEY);
  csrfToken = null;
};

// ---------------------------------------------------------------------------
// Authentication State
// ---------------------------------------------------------------------------

/**
 * Check if user is authenticated.
 * Note: httpOnly cookies cannot be read by JavaScript, so we use
 * the cached auth-user record as the client-side indicator.
 * Actual token validation always happens server-side.
 */
export const isAuthenticated = () => {
  return getAuthUser() !== null;
};

/**
 * Check if the user likely has an access token cookie.
 * Since the cookie is httpOnly, JS cannot read it directly.
 * We rely on the auth-user record set during login as a proxy.
 */
export const hasAccessToken = () => {
  return getAuthUser() !== null;
};

// ---------------------------------------------------------------------------
// Backward compatibility exports (for existing code)
// ---------------------------------------------------------------------------
export const getAccessToken = () => {
  // Return a placeholder - actual token is in httpOnly cookie
  return hasAccessToken() ? "cookie-based-auth" : "";
};

export const getRefreshToken = () => {
  // Return a placeholder - actual token is in httpOnly cookie (not readable from JS)
  return hasAccessToken() ? "cookie-based-auth" : "";
};

export const saveTokens = ({ access_token, refresh_token }) => {
  // No-op - tokens are now in httpOnly cookies
  console.log("Tokens are now stored in httpOnly cookies");
};

export const clearTokens = () => {
  // Clear user data and session
  clearAuthUser();
  clearSession();
};

// ---------------------------------------------------------------------------
// Check if token is valid (for backward compatibility)
// ---------------------------------------------------------------------------
export const isTokenValid = () => {
  return hasAccessToken();
};

// ---------------------------------------------------------------------------
// Login / Logout / Refresh
// ---------------------------------------------------------------------------
const AUTH_BASE = () => `${getApiBaseUrl()}/secure-auth`;

/**
 * Enhanced login with cookie-based auth and preferences.
 */
export const loginApi = async (username, password) => {
  try {
    const res = await fetch(`${AUTH_BASE()}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // Important: include cookies
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || "Login failed");
    }

    const data = await res.json();

    // Reset auth logout guard on successful login
    resetAuthLogoutGuard();

    // Save CSRF token
    if (data.csrf_token) {
      setCSRFToken(data.csrf_token);
    }

    // Save session ID
    if (data.session_id) {
      saveSessionId(data.session_id);
    }

    // Save user info (not tokens!)
    const userInfo = {
      user_id: data.user_id,
      username: data.username || username,
      login_id: data.login_id,
      roles: data.roles || ["user"],
      email: data.email,
      must_reset_password: data.must_reset_password || false
    };
    saveAuthUser(userInfo);

    // Cache preferences if provided
    if (data.user_preferences && preferenceService) {
      preferenceService.cache = data.user_preferences;
      preferenceService.cacheExpiry = Date.now() + preferenceService.cacheTimeout;
    }

    // Migrate any legacy preferences
    if (data.user_id && preferenceService) {
      await preferenceService.migrateLegacyPreferences(data.user_id);
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Refresh access token using cookie-based refresh token.
 */
export const refreshAccessToken = async () => {
  try {
    const res = await fetch(`${AUTH_BASE()}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // Include cookies
    });

    if (!res.ok) return false;

    const data = await res.json();

    // Update CSRF token if provided
    if (data.csrf_token) {
      setCSRFToken(data.csrf_token);
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Enhanced logout with cookie cleanup.
 */
export const logoutApi = async () => {
  try {
    const token = getCSRFToken();
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["X-CSRF-Token"] = token;
    }

    await fetch(`${AUTH_BASE()}/logout`, {
      method: "POST",
      headers,
      credentials: "include",
    });
  } catch (error) {
    console.error("Logout request failed:", error);
  } finally {
    // Clear local data regardless
    clearAuthUser();
    clearSession();
    if (preferenceService) {
      preferenceService.clearCache();
    }
  }
};

/**
 * Change password with CSRF protection.
 */
export const changePasswordApi = async (currentPassword, newPassword) => {
  const token = getCSRFToken();
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["X-CSRF-Token"] = token;
  }

  const res = await fetch(`${AUTH_BASE()}/change-password`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || "Password change failed");
  }

  return res.json();
};

// ---------------------------------------------------------------------------
// Authenticated Fetch with Cookie Auth and CSRF
// ---------------------------------------------------------------------------

/**
 * Enhanced fetch with automatic cookie auth and CSRF token.
 */
export const authFetch = async (url, options = {}) => {
  // If no auth user record exists, the user has never logged in (or was cleared).
  // Try a silent refresh first; if that also fails, signal logout via event.
  if (!hasAccessToken()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      dispatchAuthLogout();
      throw new Error('Authentication required');
    }
  }

  // Add CSRF token for state-changing requests
  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const token = getCSRFToken();
    if (token) {
      options.headers = {
        ...options.headers,
        'X-CSRF-Token': token,
      };
    }
  }

  // Add session ID if available
  const sessionId = getSessionId();
  if (sessionId) {
    options.headers = {
      ...options.headers,
      'X-Session-Id': sessionId,
    };
  }

  // Always include credentials for cookie auth
  options.credentials = 'include';

  const response = await fetch(url, options);

  // Handle 401 - try to refresh
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry the request
      return fetch(url, options);
    } else {
      // Signal logout via event (no page reload) — guarded against cascading calls
      dispatchAuthLogout();
      throw new Error('Authentication required');
    }
  }

  // Handle 403 - CSRF token might be invalid
  if (response.status === 403) {
    const cloned = response.clone();
    const text = await cloned.text();
    if (text.includes('CSRF')) {
      console.error('CSRF validation failed. Please refresh the page.');
    }
  }

  return response;
};

/**
 * Enhanced fetch with timeout and auth.
 */
export const authFetchWithTimeout = async (url, options = {}, timeoutMs = 60000) => {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await authFetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timerId);
  }
};

// ---------------------------------------------------------------------------
// Session Management API
// ---------------------------------------------------------------------------

/**
 * Get user's active sessions.
 */
export const getUserSessions = async () => {
  const response = await authFetch(`${AUTH_BASE()}/sessions`);
  if (!response.ok) {
    throw new Error('Failed to fetch sessions');
  }
  return response.json();
};

/**
 * Revoke a specific session.
 */
export const revokeSession = async (sessionId) => {
  const response = await authFetch(`${AUTH_BASE()}/sessions/${sessionId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to revoke session');
  }
  return response.json();
};

/**
 * Revoke all sessions except current.
 */
export const revokeAllSessions = async (exceptCurrent = true) => {
  const response = await authFetch(`${AUTH_BASE()}/sessions?except_current=${exceptCurrent}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to revoke sessions');
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Password Validation API
// ---------------------------------------------------------------------------

/**
 * Get password requirements.
 */
export const getPasswordRequirements = async () => {
  const response = await fetch(`${AUTH_BASE()}/password-requirements`);
  if (!response.ok) {
    throw new Error('Failed to fetch password requirements');
  }
  return response.json();
};

/**
 * Validate password strength.
 */
export const validatePassword = async (password) => {
  const response = await authFetch(`${AUTH_BASE()}/validate-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    throw new Error('Failed to validate password');
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Export all functions (including backward compatibility)
// ---------------------------------------------------------------------------
export default {
  // CSRF
  getCSRFToken,
  setCSRFToken,

  // User
  saveAuthUser,
  getAuthUser,
  clearAuthUser,

  // Session
  saveSessionId,
  getSessionId,
  clearSession,

  // Auth state
  isAuthenticated,
  hasAccessToken,
  isTokenValid,

  // Backward compatibility
  getAccessToken,
  getRefreshToken,
  saveTokens,
  clearTokens,

  // API
  loginApi,
  refreshAccessToken,
  logoutApi,
  changePasswordApi,
  authFetch,
  authFetchWithTimeout,

  // Session management
  getUserSessions,
  revokeSession,
  revokeAllSessions,

  // Password
  getPasswordRequirements,
  validatePassword,
};
